/**
 * MacroIndex — level 1 of the drill-down: the manual's table of contents.
 *
 * Full rewrite (TASK-016, 2026-07-22) — replaces the "indice annotato" with
 * a left-hand gutter of relation braces. That version measured badly on
 * real data (50 macro edges → 43 distinct chapter pairs, 23 overlapping —
 * a hairball even restrained to hover-only) and, more fundamentally, was
 * bocciata by Daniele on WHAT it was for: the Mappa isn't a relations
 * diagram, it's "un indice ragionato" — capire di cosa parla il capitolo,
 * poi saltarci dentro. No arcs, no braces, no `examWeight` encoding (which
 * is 0 on every macro/cross edge in the real data anyway — a pipeline gap,
 * not this component's problem to visualise around). Chapters are read
 * exactly like a well-made manual's table of contents: number, title, and
 * the chapter's own synthesis line, always visible.
 *
 * All row drawing now lives in `IndexTrail` — this component's only job is
 * turning macro `ConceptNode`s into `TrailItem[]` (id = `chapterId`, since
 * that's what navigation needs, and it's 1:1 with the node) and passing the
 * curriculum order through as `ordinal`.
 */
import { useMemo } from 'react'
import Text from '@arianna/design-system/Text'
import type { ConceptNode } from '@arianna/shared'
import { IndexTrail, TRAIL_MAX_WIDTH, type TrailItem } from './IndexTrail.js'

export interface MacroIndexProps {
  /** Already filtered to level 'macro', in curriculum order. */
  nodes: ConceptNode[]
  onSelectChapter: (chapterId: string) => void
}

export function MacroIndex({ nodes, onSelectChapter }: MacroIndexProps) {
  const items: TrailItem[] = useMemo(
    () =>
      nodes.map((node, index) => ({
        id: node.chapterId,
        ordinal: index + 1,
        title: node.label,
        blurb: node.summary,
      })),
    [nodes],
  )

  if (nodes.length === 0) {
    return (
      <div data-testid="concept-map-macro-index" style={{ width: '100%' }}>
        <Text
          variant="body"
          style={{ display: 'block', textAlign: 'center', padding: 'var(--space-6) 0' }}
        >
          Nessun capitolo nella mappa.
        </Text>
      </div>
    )
  }

  return (
    <div
      data-testid="concept-map-macro-index"
      style={{ width: '100%', maxWidth: TRAIL_MAX_WIDTH, marginInline: 'auto' }}
    >
      <IndexTrail
        items={items}
        onSelect={onSelectChapter}
        itemTestId="concept-map-macro-node"
        ariaLabel="Capitoli del manuale"
      />
    </div>
  )
}
